import { useEffect, useState, Fragment } from "react";
import { api, formatApiErrorDetail } from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { Check, Minus, Plus, Trash2, Users, MessageCircle, Send } from "lucide-react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";

// element: key, label, designer view/edit, client view/edit, clientToggleable
export const PROJECT_ELEMENTS = [
  { key: "overview", label: "Overview", dView: true, dEdit: true, cView: true, cEdit: false, toggleable: false },
  { key: "budget", label: "Budget", dView: true, dEdit: true, cView: true, cEdit: false, toggleable: true },
  { key: "approvals", label: "Approvals", dView: true, dEdit: true, cView: true, cEdit: true, toggleable: true },
  { key: "ffe", label: "FF&E", dView: true, dEdit: true, cView: true, cEdit: false, toggleable: true },
  { key: "po", label: "Purchase Orders", dView: true, dEdit: true, cView: false, cEdit: false, toggleable: false },
  { key: "invoices", label: "Invoices", dView: true, dEdit: true, cView: false, cEdit: false, toggleable: false },
  { key: "checklists", label: "Checklists / Assignments", dView: true, dEdit: true, cView: false, cEdit: false, toggleable: true },
  { key: "time", label: "Time Tracking", dView: true, dEdit: true, cView: false, cEdit: false, toggleable: false },
  { key: "documents", label: "Documents", dView: true, dEdit: true, cView: true, cEdit: false, toggleable: true },
  { key: "moodboards", label: "Mood Boards", dView: true, dEdit: true, cView: true, cEdit: true, toggleable: true },
  { key: "scheme", label: "Design Scheme", dView: true, dEdit: true, cView: true, cEdit: false, toggleable: true },
  { key: "communication", label: "Notes", dView: true, dEdit: true, cView: true, cEdit: true, toggleable: true },
  { key: "calendar", label: "Calendar", dView: true, dEdit: true, cView: true, cEdit: false, toggleable: true },
  { key: "photos", label: "Photos", dView: true, dEdit: true, cView: true, cEdit: true, toggleable: true },
  { key: "assistant", label: "AI Assistant", dView: true, dEdit: true, cView: true, cEdit: true, toggleable: true },
];

const Yes = ({ on }) => on
  ? <Check size={14} strokeWidth={2} className="text-[#506B52] mx-auto" />
  : <Minus size={14} strokeWidth={2} className="text-[#C9BBAE] mx-auto" />;

export default function SettingsDialog({ open, onOpenChange }) {
  const { user, setUser } = useAuth();
  const [hidden, setHidden] = useState([]);
  const [hideTrade, setHideTrade] = useState(false);
  const [busy, setBusy] = useState(false);
  const [billing, setBilling] = useState(null);
  const [cancelBusy, setCancelBusy] = useState(false);
  const [config, setConfig] = useState(null);
  const [team, setTeam] = useState({ is_owner: false, members: [] });
  const [invite, setInvite] = useState({ email: "", name: "", role_label: "" });
  const [inviteBusy, setInviteBusy] = useState(false);
  const [lastInviteLink, setLastInviteLink] = useState("");
  const isAdmin = user?.role === "admin";
  const [supportThread, setSupportThread] = useState([]);
  const [supportMsg, setSupportMsg] = useState("");
  const [supportBusy, setSupportBusy] = useState(false);
  const [threads, setThreads] = useState([]);
  const [activeThread, setActiveThread] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [replyBusy, setReplyBusy] = useState(false);

  const fmtTime = (iso) => { try { return new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); } catch { return ""; } };

  const loadSupport = () => {
    if (isAdmin) api.get("/support/threads").then((r) => setThreads(r.data)).catch(() => {});
    else api.get("/support/messages").then((r) => setSupportThread(r.data)).catch(() => {});
  };

  const sendSupport = async (e) => {
    e.preventDefault();
    const body = supportMsg.trim();
    if (!body) return;
    setSupportBusy(true);
    try {
      const { data } = await api.post("/support/messages", { message: body });
      setSupportThread((p) => [...p, data]);
      setSupportMsg("");
      toast.success("Message sent — we'll get back to you here");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSupportBusy(false); }
  };

  const sendReply = async (e) => {
    e.preventDefault();
    const body = replyText.trim();
    if (!body || !activeThread) return;
    setReplyBusy(true);
    try {
      const { data } = await api.post(`/support/threads/${activeThread.designer_id}/reply`, { message: body });
      setActiveThread((t) => ({ ...t, messages: [...t.messages, data] }));
      setThreads((arr) => arr.map((t) => (t.designer_id === activeThread.designer_id ? { ...t, messages: [...t.messages, data] } : t)));
      setReplyText("");
      toast.success("Reply sent");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setReplyBusy(false); }
  };

  const loadTeam = () => api.get("/team").then((r) => setTeam(r.data)).catch(() => {});

  const addMember = async (e) => {
    e.preventDefault();
    if (!invite.email.trim() || !invite.name.trim()) { toast.error("Name and email are required"); return; }
    setInviteBusy(true);
    try {
      const { data } = await api.post("/team/invite", invite);
      const link = `${window.location.origin}/accept-invite?token=${data.invite_token}`;
      setLastInviteLink(link);
      setInvite({ email: "", name: "", role_label: "" });
      loadTeam();
      toast.success("Team member added — share the invite link below");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setInviteBusy(false); }
  };

  const removeMember = async (id) => {
    try { await api.delete(`/team/${id}`); loadTeam(); toast.success("Team member removed"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const seatPrice = () => {
    const c = config?.currency || "CAD";
    return new Intl.NumberFormat("en-US", { style: "currency", currency: c, minimumFractionDigits: 0 }).format((config?.seat_price || 1500) / 100);
  };

  const fmtMoney = (cents) => {
    const cur = billing?.currency || config?.currency || "CAD";
    return new Intl.NumberFormat("en-US", { style: "currency", currency: cur, minimumFractionDigits: 0 }).format((cents || 0) / 100);
  };

  useEffect(() => {
    if (open) {
      setHidden(user?.client_hidden_sections || []);
      setHideTrade(!!user?.client_hide_trade_markup);
      api.get("/billing/status").then((r) => setBilling(r.data)).catch(() => setBilling(null));
      api.get("/billing/config").then((r) => setConfig(r.data)).catch(() => {});
      setLastInviteLink("");
      loadTeam();
      loadSupport();
    }
  }, [open, user]);

  const cancelSubscription = async () => {
    setCancelBusy(true);
    try {
      const { data } = await api.post("/billing/cancel");
      setBilling(data);
      toast.success("Your subscription has been canceled");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not cancel");
    } finally { setCancelBusy(false); }
  };

  const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); } catch { return ""; } };

  const clientVisible = (key) => !hidden.includes(key);
  const toggle = (key) =>
    setHidden((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/me/visibility-settings", { client_hidden_sections: hidden, client_hide_trade_markup: hideTrade });
      setUser(data);
      toast.success("Visibility settings saved");
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save settings");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none border-[#EBE5E0] bg-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="settings-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-[#2B2622]">Project Settings</DialogTitle>
          <DialogDescription className="text-sm text-[#5C5651]">
            Review what each role can view and edit, and choose which elements your clients can see. Applies to all your projects.
          </DialogDescription>
        </DialogHeader>

        {billing?.requires_subscription && (
          <div className="border border-[#EBE5E0] bg-[#F9F8F6] p-4" data-testid="membership-panel">
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#A68A6B] mb-1">Membership</p>
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="text-sm text-[#2B2622]">
                {billing.trialing ? (
                  <span data-testid="membership-status">Free trial — {billing.plan || "monthly"} plan, renews {fmtDate(billing.trial_end)}</span>
                ) : billing.status === "ACTIVE" ? (
                  <span data-testid="membership-status">Active — {billing.plan || "monthly"} plan</span>
                ) : (
                  <span data-testid="membership-status">{billing.status ? `Status: ${billing.status}` : "No active subscription"}</span>
                )}
              </div>
              {(billing.trialing || billing.status === "ACTIVE") && (
                <Button data-testid="cancel-subscription-btn" onClick={cancelSubscription} disabled={cancelBusy}
                  variant="outline" className="rounded-none border-[#9E4747] text-[#9E4747] hover:bg-[#FBEDED] uppercase tracking-wider text-xs">
                  {cancelBusy ? "Canceling…" : "Cancel subscription"}
                </Button>
              )}
            </div>

            {!billing.is_team_member && billing.base_price != null && (
              <div className="mt-4 border-t border-[#EBE5E0] pt-4" data-testid="billing-dashboard">
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#A68A6B] mb-2">Billing summary</p>
                <div className="space-y-1.5 text-sm text-[#2B2622]">
                  <div className="flex items-center justify-between">
                    <span>{billing.plan === "annual" ? "Annual" : "Monthly"} membership</span>
                    <span data-testid="billing-base">{fmtMoney(billing.base_price)}/{billing.base_interval}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Team seats — {billing.seat_count} × {fmtMoney(billing.seat_price)}</span>
                    <span data-testid="billing-seats">{fmtMoney(billing.seat_total)}/mo</span>
                  </div>
                  <div className="flex items-center justify-between border-t border-[#EBE5E0] pt-1.5 mt-1.5 font-medium">
                    <span>Monthly total{billing.plan === "annual" ? " (seats only; membership billed annually)" : ""}</span>
                    <span data-testid="billing-monthly-total">{fmtMoney(billing.monthly_total)}/mo</span>
                  </div>
                  {billing.renewal_date && (
                    <div className="flex items-center justify-between text-[#5C5651] pt-1">
                      <span>{billing.trialing ? "Trial ends / first charge" : "Next renewal"}</span>
                      <span data-testid="billing-renewal">{fmtDate(billing.renewal_date)}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {team.is_owner && (
          <div className="border border-[#EBE5E0] bg-white p-4" data-testid="team-panel">
            <div className="flex items-center gap-2 mb-1">
              <Users size={15} className="text-[#A68A6B]" />
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#A68A6B]">Team members</p>
            </div>
            <p className="text-xs text-[#5C5651] mb-3">
              Add teammates who can access all your projects. {seatPrice()}/month per member, billed to your card.
            </p>

            <div className="space-y-2">
              {team.members.length === 0 && <p className="text-sm text-[#9b938b]">No team members yet.</p>}
              {team.members.map((m) => (
                <div key={m.id} className="flex items-center justify-between border border-[#EBE5E0] px-3 py-2" data-testid={`team-member-${m.id}`}>
                  <div>
                    <span className="text-sm text-[#2B2622]">{m.name}</span>
                    {m.role_label && <span className="ml-2 text-[10px] uppercase tracking-wider text-[#A68A6B] border border-[#EBE5E0] px-1.5 py-0.5">{m.role_label}</span>}
                    {m.invite_pending && <span className="ml-2 text-[10px] uppercase tracking-wider text-[#C49A4A]">Invite pending</span>}
                    {!m.invite_pending && m.seat_status && (
                      <span data-testid={`seat-status-${m.id}`}
                        className={`ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 ${m.seat_status === "ACTIVE" ? "text-[#506B52] border border-[#506B52]" : "text-[#9E4747] border border-[#9E4747]"}`}>
                        {m.seat_status}
                      </span>
                    )}
                    <p className="text-[11px] text-[#9b938b]">{m.email}</p>
                  </div>
                  <button data-testid={`remove-member-${m.id}`} onClick={() => removeMember(m.id)}
                    className="text-[#5C5651] hover:text-[#9E4747]"><Trash2 size={15} strokeWidth={1.5} /></button>
                </div>
              ))}
            </div>

            <form onSubmit={addMember} className="mt-4 grid sm:grid-cols-3 gap-2">
              <Input data-testid="invite-name-input" value={invite.name} onChange={(e) => setInvite({ ...invite, name: e.target.value })}
                placeholder="Full name" className="rounded-none border-[#EBE5E0] focus-visible:ring-0 focus-visible:border-[#A68A6B]" />
              <Input data-testid="invite-email-input" type="email" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })}
                placeholder="Email" className="rounded-none border-[#EBE5E0] focus-visible:ring-0 focus-visible:border-[#A68A6B]" />
              <Input data-testid="invite-role-input" value={invite.role_label} onChange={(e) => setInvite({ ...invite, role_label: e.target.value })}
                placeholder="Role (e.g. Junior Designer)" className="rounded-none border-[#EBE5E0] focus-visible:ring-0 focus-visible:border-[#A68A6B]" />
              <Button data-testid="add-member-submit" type="submit" disabled={inviteBusy}
                className="sm:col-span-3 rounded-none bg-[#A68A6B] hover:bg-[#8C7154] text-white uppercase tracking-wider text-xs py-4">
                <Plus size={14} className="mr-2" /> {inviteBusy ? "Adding…" : `Add team member (+${seatPrice()}/mo)`}
              </Button>
            </form>

            {lastInviteLink && (
              <div className="mt-3 border border-[#A68A6B] bg-[#F9F6F1] p-3" data-testid="invite-link-box">
                <p className="text-[11px] uppercase tracking-wider text-[#8C7154] mb-1">Share this invite link</p>
                <p className="text-[12px] text-[#2B2622] break-all font-mono">{lastInviteLink}</p>
              </div>
            )}
          </div>
        )}

        {!isAdmin && (<>
        <div className="max-h-[60vh] overflow-y-auto border border-[#EBE5E0]">
          <table className="w-full text-sm">
            <thead className="bg-[#F9F8F6] sticky top-0">
              <tr className="text-left text-[10px] uppercase tracking-wider text-[#A68A6B]">
                <th className="p-3 font-medium">Element</th>
                <th className="p-3 font-medium text-center">Designer<br/>View</th>
                <th className="p-3 font-medium text-center">Designer<br/>Edit</th>
                <th className="p-3 font-medium text-center">Client<br/>View</th>
                <th className="p-3 font-medium text-center">Client<br/>Edit</th>
                <th className="p-3 font-medium text-right">Visible to client</th>
              </tr>
            </thead>
            <tbody>
              {PROJECT_ELEMENTS.map((el) => (
                <Fragment key={el.key}>
                <tr className="border-t border-[#F2EFEB]" data-testid={`settings-row-${el.key}`}>
                  <td className="p-3 text-[#2B2622]">{el.label}</td>
                  <td className="p-3"><Yes on={el.dView} /></td>
                  <td className="p-3"><Yes on={el.dEdit} /></td>
                  <td className="p-3"><Yes on={el.toggleable ? clientVisible(el.key) && el.cView : el.cView} /></td>
                  <td className="p-3"><Yes on={el.cEdit && (!el.toggleable || clientVisible(el.key))} /></td>
                  <td className="p-3 text-right">
                    {el.toggleable ? (
                      <Switch data-testid={`settings-toggle-${el.key}`} checked={clientVisible(el.key)}
                        onCheckedChange={() => toggle(el.key)}
                        className="data-[state=checked]:bg-[#A68A6B]" />
                    ) : (
                      <span className="text-[10px] uppercase tracking-wider text-[#9b938b]">
                        {el.cView ? "Always" : "Designer only"}
                      </span>
                    )}
                  </td>
                </tr>
                {el.key === "budget" && clientVisible("budget") && (
                  <tr className="border-t border-[#F2EFEB] bg-[#FBFAF8]" data-testid="settings-subrow-budget-trade">
                    <td className="py-2.5 pl-8 pr-3 text-[#5C5651] text-xs" colSpan={5}>
                      ↳ Trade/Net Cost &amp; Markup Visible
                    </td>
                    <td className="p-3 text-right">
                      <Switch data-testid="settings-toggle-trade-visible" checked={!hideTrade}
                        onCheckedChange={(v) => setHideTrade(!v)}
                        className="data-[state=checked]:bg-[#A68A6B]" />
                    </td>
                  </tr>
                )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>

        <Button data-testid="settings-save-btn" onClick={save} disabled={busy}
          className="w-full rounded-none bg-[#A68A6B] hover:bg-[#8C7154] text-white uppercase tracking-wider text-xs py-5">
          {busy ? "Saving…" : "Save Settings"}
        </Button>
        </>
        )}

        {isAdmin ? (
          <div className="border border-[#EBE5E0] bg-white" data-testid="support-inbox">
            <div className="flex items-center gap-2 px-4 pt-4">
              <MessageCircle size={15} className="text-[#A68A6B]" />
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#A68A6B]">Support inbox</p>
            </div>
            {threads.length === 0 ? (
              <p className="text-sm text-[#9b938b] px-4 py-6">No messages from designers yet.</p>
            ) : !activeThread ? (
              <div className="p-4 space-y-2">
                {threads.map((t) => {
                  const last = t.messages[t.messages.length - 1];
                  return (
                    <button key={t.designer_id} data-testid={`support-thread-${t.designer_id}`} onClick={() => { setActiveThread(t); setReplyText(""); }}
                      className="w-full text-left border border-[#EBE5E0] hover:border-[#A68A6B] px-3 py-2 transition-colors">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-[#2B2622] font-medium">{t.designer_name || t.designer_email}</span>
                        <span className="text-[10px] uppercase tracking-wider text-[#9b938b]">{fmtTime(last.created_at)}</span>
                      </div>
                      <p className="text-xs text-[#5C5651] truncate mt-0.5">{last.sender === "support" ? "You: " : ""}{last.body}</p>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="p-4">
                <button onClick={() => setActiveThread(null)} className="text-[11px] uppercase tracking-wider text-[#A68A6B] hover:text-[#8C7154] mb-3">← All messages</button>
                <p className="text-sm text-[#2B2622] font-medium">{activeThread.designer_name} <span className="text-[#9b938b] font-normal">· {activeThread.designer_email}</span></p>
                <div className="mt-3 space-y-2 max-h-64 overflow-y-auto pr-1">
                  {activeThread.messages.map((m) => (
                    <div key={m.id} className={`flex ${m.sender === "support" ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[80%] px-3 py-2 text-sm ${m.sender === "support" ? "bg-[#A68A6B] text-white" : "bg-[#F2EFEB] text-[#2B2622]"}`}>
                        <p className="whitespace-pre-wrap">{m.body}</p>
                        <p className={`text-[10px] mt-1 ${m.sender === "support" ? "text-white/70" : "text-[#9b938b]"}`}>{fmtTime(m.created_at)}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <form onSubmit={sendReply} className="mt-3 flex items-end gap-2">
                  <Textarea data-testid="support-reply-input" value={replyText} onChange={(e) => setReplyText(e.target.value)} rows={2}
                    placeholder="Write a reply…" className="rounded-none border-[#EBE5E0] focus-visible:ring-0 focus-visible:border-[#A68A6B]" />
                  <Button type="submit" disabled={replyBusy} data-testid="support-reply-submit"
                    className="rounded-none bg-[#A68A6B] hover:bg-[#8C7154] text-white px-4 py-5"><Send size={16} /></Button>
                </form>
              </div>
            )}
          </div>
        ) : (
          <div className="border border-[#EBE5E0] bg-white" data-testid="support-panel">
            <div className="flex items-center gap-2 px-4 pt-4">
              <MessageCircle size={15} className="text-[#A68A6B]" />
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#A68A6B]">Help &amp; Support</p>
            </div>
            <p className="text-xs text-[#5C5651] px-4 mt-1">Questions or need a hand with the app? Send us a message — replies appear right here.</p>
            <div className="px-4 mt-3 space-y-2 max-h-64 overflow-y-auto" data-testid="support-messages">
              {supportThread.length === 0 ? (
                <p className="text-sm text-[#9b938b] py-3">No messages yet. Start the conversation below.</p>
              ) : (
                supportThread.map((m) => (
                  <div key={m.id} className={`flex ${m.sender === "designer" ? "justify-end" : "justify-start"}`}>
                    <div data-testid={`support-msg-${m.sender}`} className={`max-w-[80%] px-3 py-2 text-sm ${m.sender === "designer" ? "bg-[#A68A6B] text-white" : "bg-[#F2EFEB] text-[#2B2622]"}`}>
                      <p className="whitespace-pre-wrap">{m.body}</p>
                      <p className={`text-[10px] mt-1 ${m.sender === "designer" ? "text-white/70" : "text-[#9b938b]"}`}>
                        {m.sender === "support" ? "WorkRoom · " : ""}{fmtTime(m.created_at)}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
            <form onSubmit={sendSupport} className="p-4 flex items-end gap-2">
              <Textarea data-testid="support-message-input" value={supportMsg} onChange={(e) => setSupportMsg(e.target.value)} rows={2}
                placeholder="Ask a question or describe what you need help with…"
                className="rounded-none border-[#EBE5E0] focus-visible:ring-0 focus-visible:border-[#A68A6B]" />
              <Button type="submit" disabled={supportBusy} data-testid="support-send-btn"
                className="rounded-none bg-[#A68A6B] hover:bg-[#8C7154] text-white px-4 py-5"><Send size={16} /></Button>
            </form>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
